import expressConfig from "src/config/express.json";
import axios from "axios";

const env = process.env.NODE_ENV || "development";
const config = expressConfig[env];

const generalEndPoint = "https://graph.facebook.com/v23.0/";

export const getFacebookToken = async (code: string) => {
  try {
    const response = await axios.get(`${generalEndPoint}oauth/access_token`, {
      params: {
        client_id: config.facebook_app_id,
        client_secret: config.FACEBOOK_APP_SECRET,
        code,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Facebook access token:", error);
    throw error;
  }
};

export const getUserData = async (accessToken: string) => {
  try {
    const user = await axios.get(
      `${generalEndPoint}me?access_token=${accessToken}&fields=id,name,email,picture.width(640).height(640)`,
    );
    return user.data;
  } catch (error) {
    console.error(
      "Error fetching Facebook user data during whatsapp flow process",
      error,
    );
    return null;
  }
};

export const getWhatsAppBusinessProfile = async (
  phoneNumberId: string,
  accessToken: string,
) => {
  try {
    const response = await axios.get(
      `${generalEndPoint}${phoneNumberId}/whatsapp_business_profile`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data; // Profile data here
  } catch (error: any) {
    console.error(
      "Error fetching WhatsApp Business Profile:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

export const getWabaDetails = async (wabaId: string, accessToken: string) => {
  try {
    const url = `${generalEndPoint}${wabaId}?fields=name,status,business_verification_status&access_token=${accessToken}`;
    // const url = `${generalEndPoint}${wabaId}?fields=name,status,business_verification_status,currency,timezone_id,primary_funding_id,account_review_status&access_token=${accessToken}`;
    const response = await fetch(url);
    const data = await response.json();

    if (data.error) {
      throw new Error(data.error.message); // Meta API-specific error
    }

    return data; // ? Return WABA details
  } catch (error: any) {
    console.error(
      "Error fetching WABA details:",
      error.response?.data || error.message,
    );
    throw error; // ?? Pass to higher-level handler or API response
  }
};

export const getPhoneNumberDetails = async (
  phoneNumberId: string,
  accessToken: string,
) => {
  try {
    const url = `${generalEndPoint}${phoneNumberId}?fields=verified_name,display_phone_number,status,code_verification_status,quality_rating,webhook_configuration&access_token=${accessToken}`;
    const response = await fetch(url);
    const data = await response.json();

    if (data.error) {
      throw new Error(data.error.message);
    }

    return data; // ? Full phone number details
  } catch (error: any) {
    console.error(
      "Error fetching phone number details:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

export const subscribeAppToWaba = async (
  wabaId: string,
  businessToken: string,
) => {
  const url = `${generalEndPoint}${wabaId}/subscribed_apps`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${businessToken}`,
    },
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error.message);
  return data.success;
};

export const registerPhoneNumber = async (
  phoneNumberId: string,
  businessToken: string,
  pin: string,
) => {
  const url = `${generalEndPoint}${phoneNumberId}/register`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${businessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      messaging_product: "whatsapp",
      pin,
    }),
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error.message);
  return data.success;
};

// Turns on WhatsApp Calling for the phone number. Separate from the
// app-level `calls` webhook subscription (which is set once in the Meta
// dashboard) — this per-number setting is what actually surfaces the
// call icon to customers on the business profile. Meta requires a
// messaging limit of at least 2000/day; caller should handle failures
// non-fatally since the number is still messaging-usable without it.
export const enableCallingOnPhoneNumber = async (
  phoneNumberId: string,
  businessToken: string,
) => {
  const url = `${generalEndPoint}${phoneNumberId}/settings`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${businessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      calling: { status: "ENABLED" },
    }),
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error.message);
  return data.success;
};

// Fetch the current call settings for a phone number. Used to load the
// existing state into the Call Settings modal before the admin edits.
export const getCallSettings = async (
  phoneNumberId: string,
  businessToken: string,
) => {
  const url = `${generalEndPoint}${phoneNumberId}/settings?fields=calling`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${businessToken}`,
    },
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error.message);
  return data;
};

// POST an arbitrary calling settings object. The caller composes the
// full `calling` shape (voicemail, call_hours, etc.); Meta's endpoint
// is a partial-update — only the fields present in the body change,
// so it's safe to send just one section (voicemail-only, hours-only).
export const updateCallSettings = async (
  phoneNumberId: string,
  businessToken: string,
  callingObject: Record<string, unknown>,
) => {
  const url = `${generalEndPoint}${phoneNumberId}/settings`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${businessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ calling: callingObject }),
  });
  const data = await response.json();
  if (data.error) {
    // Meta's `message` is often generic ("Invalid parameter"); the
    // useful text lives in error_user_msg (e.g. "File format is
    // invalid. Only audio/ogg mime type is allowed").
    const detail =
      data.error.error_user_msg ||
      data.error.error_user_title ||
      data.error.message ||
      "Settings update failed";
    throw new Error(detail);
  }
  return data.success;
};

// Upload an audio file to WhatsApp's Media API. Returns the media_id.
//
// For messaging media (voice notes, images, etc.): omit useCase.
// For voicemail announcements: pass useCase="call_voicemail_announcement"
// AND a description. This is REQUIRED by Meta — messaging media_ids are
// NOT valid for voicemail; the /settings endpoint rejects them with
// "Media was not uploaded for voicemail". Voicemail media also skips
// the standard 30-day TTL applied to messaging media.
//
// Voicemail-specific constraints (per Meta docs):
//   - MIME must be audio/ogg with OPUS codec
//   - Duration must be under 60 seconds
// Upload a voicemail announcement via our own backend. The backend
// runs ffmpeg to normalize whatever the user provided (MP3, WAV,
// WebM/opus from mic recording, OGG-Vorbis, etc.) into OGG/OPUS at
// exactly the codec/bitrate Meta requires, then forwards to Meta's
// Media API with use_case=CALL_VOICEMAIL_ANNOUNCEMENT. The WhatsApp
// business token stays server-side — never exposed to the browser.
//
// Returns Meta's media_id.
export const uploadVoicemailViaBackend = async (
  phoneNumberId: string,
  file: Blob,
  filename: string,
) => {
  // Uses the module-scope `config` (top of file) — same domain/port
  // resolved once per bundle. Backend route is defined in
  // backend/routes/whatsappRoutes.js.
  const backendUrl = `${config.graphql_domain}${
    config.port ? `:${config.port}` : ""
  }/whatsapp/voicemail-upload`;

  const form = new FormData();
  form.append("phoneNumberId", phoneNumberId);
  form.append("audio", file, filename);

  const response = await fetch(backendUrl, {
    method: "POST",
    credentials: "include",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`,
    },
    body: form,
  });
  const data = await response.json();
  if (!response.ok || data.success === 0 || !data.mediaId) {
    throw new Error(
      data?.error ||
        `Backend rejected voicemail upload (HTTP ${response.status})`,
    );
  }
  return data.mediaId as string;
};

export const uploadWhatsappMedia = async (
  phoneNumberId: string,
  businessToken: string,
  file: Blob,
  mimetype: string,
  filename: string,
  useCase?: string,
  description?: string,
) => {
  const url = `${generalEndPoint}${phoneNumberId}/media`;
  const form = new FormData();
  form.append("messaging_product", "whatsapp");
  // Re-wrap the Blob into a proper File with the target MIME so the
  // multipart part header carries the exact Content-Type.
  const typedFile =
    file instanceof File
      ? file
      : new File([file], filename, { type: mimetype });
  // For voicemail uploads (useCase set), the `type` form field is a
  // legacy parameter and mixing it with the newer voicemail params has
  // been flaky. The file part already carries the correct Content-Type
  // via the typed File wrapper, so we skip the redundant field.
  if (!useCase) {
    form.append("type", mimetype);
  }
  form.append("file", typedFile, filename);
  if (useCase) form.append("use_case", useCase);
  if (description) form.append("description", description);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${businessToken}`,
    },
    body: form,
  });

  // Read the response body once so we can log AND parse it.
  const raw = await response.text();
  let data: any = {};
  try {
    data = JSON.parse(raw);
  } catch {
    // fall through
  }

  if (!response.ok || data.error) {
    // eslint-disable-next-line no-console
    console.error("[uploadWhatsappMedia] Meta returned an error", {
      status: response.status,
      body: raw,
      requestUrl: url,
      useCase,
      description, // log the actual value (not just presence) — Meta's
      // parser can silently reject the whole body on non-ASCII chars
      filename,
      mimetype,
      fileType: typedFile.type,
      fileSize: typedFile.size,
    });
    const detail =
      data?.error?.error_user_msg ||
      data?.error?.error_user_title ||
      data?.error?.message ||
      raw ||
      `HTTP ${response.status}`;
    throw new Error(detail);
  }
  return data.id as string;
};

export const sendTestMessage = async (
  phoneNumberId: string,
  businessToken: string,
  toWhatsAppNumber: string,
  bodyText: string,
) => {
  const url = `${generalEndPoint}${phoneNumberId}/messages`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${businessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      messaging_product: "whatsapp",
      recipient_type: "individual",
      to: toWhatsAppNumber,
      type: "text",
      text: { body: bodyText },
    }),
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error.message);
  return data;
};
